import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { makeListResponse } from '~/mocks/utils/listFixtures';
import { axiosInstance } from '~/services';

import { receiveKeys } from './keys';

import type { Type3PLReceiveStatusInfo } from '~/types/receive.types';
import type { TypeGetInboundRequestProgressParams, TypeGetInboundRequestProgressResponse } from '~/types/receive.types';

const fetchInboundRequestProgress = async (
  ownerCode: string,
  params: TypeGetInboundRequestProgressParams
): Promise<TypeGetInboundRequestProgressResponse> => {
  const { data } = await axiosInstance.get<TypeGetInboundRequestProgressResponse>(
    `/api-inbound/v1/3pl/receiving/${ownerCode}/inbound-request/progress`,
    { params }
  );
  return data;
};

type TypeUseGetInboundRequestProgressQueryParams = {
  ownerCode: string;
  params: TypeGetInboundRequestProgressParams;
  enabled?: boolean;
};

/**
 * [입고관리 > 입고현황] 3PL 입고현황 목록 조회 Query Hook
 */
export const useGetInboundRequestProgressQuery = ({
  ownerCode,
  params,
  enabled = true,
}: TypeUseGetInboundRequestProgressQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.inboundRequestProgress(ownerCode, params),
    queryFn: () => fetchInboundRequestProgress(ownerCode, params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

// 5건에 status 3종 + reportStatus 4종 + 진행률 분기를 분산해 한 응답에 모두 노출
const PROGRESS_3PL_FIXTURES: Array<Omit<Type3PLReceiveStatusInfo, 'requestId' | 'requestCode'>> = [
  {
    status: 'PROCEEDING',
    reportStatus: 'REGISTERED',
    estimatedArrivalDate: '2026-05-13',
    estimatedArrivalTime: '10:00~12:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 5,
    qty: 120,
    receivedSkuCnt: 3,
    receivedQty: 72,
    receivedDate: null,
  },
  {
    status: 'COMPLETED',
    reportStatus: 'COMPLETED',
    estimatedArrivalDate: '2026-05-12',
    estimatedArrivalTime: '14:00~16:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 3,
    qty: 60,
    receivedSkuCnt: 3,
    receivedQty: 60,
    receivedDate: '2026-05-12T16:30:00',
  },
  {
    status: 'FORCED_CLOSED',
    reportStatus: 'CANCELED',
    estimatedArrivalDate: '2026-05-11',
    estimatedArrivalTime: '09:00~11:00',
    releaseCodeName: '밀크런(픽업)',
    supplierCode: 'SU000005',
    supplierName: '테스트 화주사_두번째 공급사',
    skuCnt: 4,
    qty: 80,
    receivedSkuCnt: 2,
    receivedQty: 40,
    receivedDate: '2026-05-11T18:00:00',
  },
  {
    status: 'PROCEEDING',
    reportStatus: 'NONE',
    estimatedArrivalDate: '2026-05-14',
    estimatedArrivalTime: '13:00~15:00',
    releaseCodeName: '컬리배차(픽업)',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    skuCnt: 2,
    qty: 40,
    receivedSkuCnt: 0,
    receivedQty: 0,
    receivedDate: null,
  },
  {
    status: 'COMPLETED',
    reportStatus: 'REGISTERED',
    estimatedArrivalDate: '2026-05-10',
    estimatedArrivalTime: '11:00~13:00',
    releaseCodeName: '컬리배차(직배송)',
    supplierCode: 'SU000006',
    supplierName: '테스트 화주사_세번째 공급사',
    skuCnt: 1,
    qty: 20,
    receivedSkuCnt: 1,
    receivedQty: 20,
    receivedDate: '2026-05-10T15:00:00',
  },
];

const makeInboundRequestProgressItem = (index: number): Type3PLReceiveStatusInfo => {
  const fixture = PROGRESS_3PL_FIXTURES[(index - 1) % PROGRESS_3PL_FIXTURES.length];
  return {
    requestId: 9100 + index,
    requestCode: `OR20260513_3PL_${String(index).padStart(5, '0')}`,
    ...fixture,
  };
};

const buildInboundRequestProgressListResponse = (count: number): TypeGetInboundRequestProgressResponse =>
  makeListResponse(count, makeInboundRequestProgressItem, 'inboundRequestProgresses', {
    listView: 50,
    listTotal: count,
    cursor: 0,
  });

registerMockAPI<TypeGetInboundRequestProgressResponse>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/inbound-request/progress',
  label: '입고/입고현황3PL/목록',
  scenarios: [
    {
      name: '정상 응답 — 5건 (status 3종 × reportStatus 4종 혼합)',
      response: buildInboundRequestProgressListResponse(5),
    },
    { name: '정상 응답 — 0건 (빈 목록)', response: buildInboundRequestProgressListResponse(0) },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
